import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { useDispatch } from 'react-redux';
import { showNotification } from '../../store/Reducers/NotificationSnackbarSlice';
import { CncService } from '../../api/services/cncService';
import { RefreshButton } from '../../components/Buttons/RefreshButton';
import {
  CncAwaitingDispatchItem,
  CncCustomerAtBayItem,
} from '../../types/api/response/cncresponse';

const POLL_INTERVAL_MS = 15000;
const DATE_FORMAT = 'MMM D, HH:mm';
const TIME_FORMAT = 'HH:mm';
const QUERY_DATE_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

const defaultStartDate = () => dayjs().subtract(1, 'day').startOf('day');
const defaultEndDate = () => dayjs().add(1, 'day').endOf('day');

export const CncDashboardPage = () => {
  const dispatch = useDispatch();

  const [awaitingDispatch, setAwaitingDispatch] = useState<CncAwaitingDispatchItem[]>([]);
  const [arrived, setArrived] = useState<CncCustomerAtBayItem[]>([]);
  const [loading, setLoading] = useState(true);

  const [startDate, setStartDate] = useState<Dayjs | null>(defaultStartDate());
  const [endDate, setEndDate] = useState<Dayjs | null>(defaultEndDate());
  const [appliedStartDate, setAppliedStartDate] = useState<Dayjs | null>(defaultStartDate());
  const [appliedEndDate, setAppliedEndDate] = useState<Dayjs | null>(defaultEndDate());
  const [dateError, setDateError] = useState('');

  const hasLoadedOnce = useRef(false);

  const validateDates = (start: Dayjs | null, end: Dayjs | null): string => {
    if (start && end && end.isBefore(start)) return 'Start date must not be after end date';
    return '';
  };

  const fetchQueues = useCallback(async () => {
    if (!hasLoadedOnce.current) setLoading(true);
    try {
      const [awaitingDispatchData, arrivedData] = await Promise.all([
        CncService.getAwaitingDispatch(
          undefined,
          appliedStartDate?.format(QUERY_DATE_FORMAT),
          appliedEndDate?.format(QUERY_DATE_FORMAT),
        ),
        CncService.getCustomerAtBay(
          undefined,
          appliedStartDate?.format(QUERY_DATE_FORMAT),
          appliedEndDate?.format(QUERY_DATE_FORMAT),
        ),
      ]);
      setAwaitingDispatch(awaitingDispatchData);
      setArrived(arrivedData);
    } catch (e: any) {
      // Keep whatever was last rendered — a transient poll failure shouldn't blank the dashboard.
      dispatch(
        showNotification({
          message: e?.response?.data?.message ?? 'Failed to refresh the CNC dashboard.',
          severity: 'error',
        }),
      );
    } finally {
      hasLoadedOnce.current = true;
      setLoading(false);
    }
  }, [dispatch, appliedStartDate, appliedEndDate]);

  const handleApplyDateRange = () => {
    const error = validateDates(startDate, endDate);
    setDateError(error);
    if (error) return;
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
  };

  useEffect(() => {
    fetchQueues();

    let intervalId: ReturnType<typeof setInterval> | null = null;

    const startPolling = () => {
      if (intervalId === null) {
        intervalId = setInterval(fetchQueues, POLL_INTERVAL_MS);
      }
    };
    const stopPolling = () => {
      if (intervalId !== null) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchQueues();
        startPolling();
      } else {
        stopPolling();
      }
    };

    startPolling();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchQueues]);

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        flexWrap="wrap"
        gap={2}
        mb={2}
      >
        <Typography variant="h5">CNC Dashboard</Typography>
        <RefreshButton onClick={fetchQueues} />
      </Box>

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box display="flex" flexWrap="wrap" gap={2} alignItems="flex-end" mb={2}>
          <DatePicker
            label="From"
            value={startDate}
            onChange={(val) => {
              setStartDate(val);
              setDateError(validateDates(val, endDate));
            }}
            slotProps={{ textField: { size: 'small', sx: { width: 175 } } }}
          />
          <DatePicker
            label="To"
            value={endDate}
            onChange={(val) => {
              setEndDate(val);
              setDateError(validateDates(startDate, val));
            }}
            slotProps={{ textField: { size: 'small', sx: { width: 175 } } }}
          />
          <Button
            variant="contained"
            onClick={handleApplyDateRange}
            disabled={!!dateError}
            sx={{ fontSize: 12, fontWeight: 600, height: 40 }}
          >
            Apply
          </Button>
          {dateError && (
            <Typography variant="caption" color="error" sx={{ width: '100%', mt: -1 }}>
              {dateError}
            </Typography>
          )}
        </Box>
      </LocalizationProvider>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="flex-start">
          <Box flex="1 1 420px" minWidth={0}>
            <Typography variant="h6" mb={1}>
              Awaiting Dispatch
            </Typography>
            {awaitingDispatch.length === 0 ? (
              <Box py={4} textAlign="center" color="text.secondary">
                <Typography variant="body2">No orders packed and waiting.</Typography>
              </Box>
            ) : (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Order</TableCell>
                      <TableCell>Customer</TableCell>
                      <TableCell>Items</TableCell>
                      <TableCell>Packed</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {awaitingDispatch.map((item) => (
                      <TableRow key={item.order_no}>
                        <TableCell>{item.order_no}</TableCell>
                        <TableCell>{item.customer_name}</TableCell>
                        <TableCell>{item.item_count}</TableCell>
                        <TableCell>{dayjs(item.created_date).format(DATE_FORMAT)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>

          <Box flex="1 1 480px" minWidth={0}>
            <Typography variant="h6" mb={1}>
              Arrived
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              Dispatch happens by building and starting the tour in Locus.
            </Typography>
            {arrived.length === 0 ? (
              <Box py={4} textAlign="center" color="text.secondary">
                <Typography variant="body2">No customers checked in yet.</Typography>
              </Box>
            ) : (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Order</TableCell>
                      <TableCell>Customer</TableCell>
                      <TableCell>Items</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Bay</TableCell>
                      <TableCell>Slot</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {arrived.map((item) => {
                      const atBay = item.order_status === 'CUSTOMER_AT_BAY';
                      return (
                        <TableRow key={item.order_no}>
                          <TableCell>{item.order_no}</TableCell>
                          <TableCell>{item.customer_name}</TableCell>
                          <TableCell>{item.item_count}</TableCell>
                          <TableCell>
                            <Chip
                              label={atBay ? 'At Bay' : 'At Gate'}
                              color={atBay ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{item.bay?.label ?? '—'}</TableCell>
                          <TableCell>
                            {item.delivery_slot
                              ? `${dayjs(item.delivery_slot.start_time).format(TIME_FORMAT)}–${dayjs(
                                  item.delivery_slot.end_time,
                                ).format(TIME_FORMAT)}`
                              : '—'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};
